<script setup lang="ts" generic="T">
/** TypeaheadPicker */
/* Type to search, pick one match, and the input clears for the next pick.
   The caller owns what has been picked (typically a list rendered below).
   Searches either a local `items` list (case-insensitive substring match) or an
   async `search` function (the server decides what matches). As in the Vue 2
   typeahead, the first match is always highlighted, so Enter picks it. */
import { computed, nextTick, onUnmounted, ref, shallowRef, watch } from 'vue'
import {
  ComboboxAnchor,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxRoot,
  ComboboxViewport
} from 'reka-ui'
import { debouncer } from '@/util/debouncer'
import { announce } from '@/util/live_announcer'
import { matchParts } from './typeahead_match'

const props = withDefaults(defineProps<{
  id: string
  itemLabel: (item: T) => string
  itemKey: (item: T) => string
  items?: T[]
  search?: (query: string, signal: AbortSignal) => Promise<T[]>
  placeholder?: string
  limit?: number
  debounceMs?: number
  disabled?: boolean
  emptyText?: string
}>(), {
  items: undefined,
  search: undefined,
  placeholder: '',
  limit: 10,
  debounceMs: 200,
  disabled: false,
  emptyText: ''
})

const emit = defineEmits<{ select: [item: T] }>()

const SEARCH_FAILED_TEXT = 'Search failed. Please try again.'
const SEARCHING_TEXT = 'Searching…'

const root = ref<{ highlightFirstItem: () => void } | null>(null)
const query = ref('')
const isMenuWanted = ref(false)
const searchResults = shallowRef<T[]>([])
const isSearching = ref(false)
const searchFailed = ref(false)
let searchController: AbortController | null = null

const trimmedQuery = computed(() => query.value.trim())

const localMatches = computed(() => {
  const needle = trimmedQuery.value.toLowerCase()
  if (!needle || !props.items) return []
  return props.items.filter(item => props.itemLabel(item).toLowerCase().includes(needle))
})

const visibleResults = computed(() =>
  (props.search ? searchResults.value : localMatches.value).slice(0, props.limit)
)

const statusText = computed(() => {
  if (visibleResults.value.length) return ''
  if (searchFailed.value) return SEARCH_FAILED_TEXT
  if (isSearching.value) return SEARCHING_TEXT
  return props.emptyText
})

const isOpen = computed(() =>
  isMenuWanted.value && Boolean(trimmedQuery.value) && (visibleResults.value.length > 0 || Boolean(statusText.value))
)

function cancelSearch() {
  debouncedSearch.value.cancel()
  searchController?.abort()
  searchController = null
  isSearching.value = false
}

async function runSearch(searchTerm?: string) {
  if (!props.search || !searchTerm) return
  searchController?.abort()
  const controller = new AbortController()
  searchController = controller
  try {
    const results = await props.search(searchTerm, controller.signal)
    if (controller.signal.aborted) return
    searchResults.value = results
    searchFailed.value = false
  } catch (error) {
    if (controller.signal.aborted) return
    console.error('Typeahead search failed:', error)
    searchResults.value = []
    searchFailed.value = true
  } finally {
    if (searchController === controller) {
      searchController = null
      isSearching.value = false
    }
  }
}

const debouncedSearch = computed(() => debouncer<string>(runSearch, props.debounceMs))
watch(debouncedSearch, (_, previous) => previous.cancel())

function searchFor(searchTerm: string) {
  cancelSearch()
  if (!searchTerm) {
    searchResults.value = []
    searchFailed.value = false
    return
  }
  isSearching.value = true
  debouncedSearch.value(searchTerm)
}

watch(trimmedQuery, searchTerm => {
  isMenuWanted.value = true
  if (props.search) searchFor(searchTerm)
})

// A new source (another standard set, say) makes the results on screen wrong.
watch(() => props.search, () => {
  searchResults.value = []
  searchFailed.value = false
  if (props.search) searchFor(trimmedQuery.value)
})

watch(visibleResults, async results => {
  if (!results.length) return
  await nextTick()
  root.value?.highlightFirstItem()
})

watch([statusText, isOpen], ([text, open]) => {
  if (open && text && text !== SEARCHING_TEXT) announce(text)
})

onUnmounted(cancelSearch)

function handlePick(key: unknown) {
  const picked = visibleResults.value.find(item => props.itemKey(item) === key)
  if (!picked) return
  query.value = ''
  emit('select', picked)
}
</script>

<template>
  <ComboboxRoot
    ref="root"
    :model-value="null"
    :open="isOpen"
    :disabled="disabled"
    :reset-search-term-on-blur="false"
    ignore-filter
    class="relative w-full"
    @update:open="open => isMenuWanted = open"
    @update:model-value="handlePick"
  >
    <ComboboxAnchor class="w-full">
      <ComboboxInput
        :id="id"
        v-model="query"
        :placeholder="placeholder"
        autocomplete="off"
        class="w-full h-10 px-3 py-2 leading-none text-md text-gray-900 bg-white border border-gray-600 rounded-xs placeholder:text-gray-700 focus:outline-primary-400 data-[disabled]:bg-gray-300 data-[disabled]:text-gray-700 data-[disabled]:cursor-not-allowed"
      />
    </ComboboxAnchor>

    <ComboboxContent class="absolute z-50 w-full mt-1 bg-white border border-gray-600 rounded-xs shadow-md">
      <ComboboxViewport class="py-1 max-h-80 overflow-y-auto">
        <ComboboxEmpty
          class="px-4 py-2 text-md"
          :class="statusText === SEARCHING_TEXT ? 'text-gray-700' : 'text-red-400'"
        >
          {{ statusText }}
        </ComboboxEmpty>

        <ComboboxItem
          v-for="item in visibleResults"
          :key="itemKey(item)"
          :value="itemKey(item)"
          class="px-4 py-2 text-md text-gray-900 cursor-pointer select-none outline-none data-[highlighted]:bg-primary-400 data-[highlighted]:text-white"
        >
          <slot name="item" :item="item">
            <template v-for="(part, index) in matchParts(itemLabel(item), trimmedQuery)" :key="index">
              <strong v-if="part.isMatch">{{ part.text }}</strong>
              <template v-else>{{ part.text }}</template>
            </template>
          </slot>
        </ComboboxItem>
      </ComboboxViewport>
    </ComboboxContent>
  </ComboboxRoot>
</template>
